import { useEffect, useRef, useState, useCallback } from 'react';
import { Folder, Search } from 'lucide-react';
import type { TransparencyProject } from '@yawi/types';
import { TransparencyProjectCategory } from '@yawi/types';
import { useServices, useToast } from '@/app/providers';
import { Loading } from '@/core/ui/components/Loading';
import { ProjectCard } from '../components/ProjectCard';
import { ProjectDetailModal } from '../components/ProjectDetailModal';

type TabType = 'active' | 'history';

const CATEGORY_OPTIONS: { value: TransparencyProjectCategory | ''; label: string }[] = [
  { value: '', label: 'Todas las categorías' },
  { value: TransparencyProjectCategory.MAINTENANCE, label: 'Mantenimiento' },
  { value: TransparencyProjectCategory.SECURITY, label: 'Seguridad' },
  { value: TransparencyProjectCategory.REMODELING, label: 'Remodelación' },
  { value: TransparencyProjectCategory.LANDSCAPING, label: 'Jardinería' },
  { value: TransparencyProjectCategory.INFRASTRUCTURE, label: 'Infraestructura' },
  { value: TransparencyProjectCategory.AMENITIES, label: 'Amenidades' },
  { value: TransparencyProjectCategory.TECHNOLOGY, label: 'Tecnología' },
  { value: TransparencyProjectCategory.OTHER, label: 'Otro' },
];

export const TransparencyPage = () => {
  const toast = useToast();
  const { transparencyProjectRepo } = useServices();
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const [activeProjects, setActiveProjects] = useState<TransparencyProject[]>([]);
  const [historyProjects, setHistoryProjects] = useState<TransparencyProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<TransparencyProjectCategory | ''>('');
  const [selectedProject, setSelectedProject] = useState<TransparencyProject | null>(null);

  // Debounce: sin él cada tecla disparaba una petición.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);
  // Solo la última petición de cada lista escribe el estado.
  const activeRequestRef = useRef(0);
  const historyRequestRef = useRef(0);

  const loadActive = useCallback(async () => {
    const requestId = ++activeRequestRef.current;
    try {
      const result = await transparencyProjectRepo.getPublishedProjects({
        limit: 50,
        search: debouncedSearch || undefined,
        category: category || undefined,
      });
      if (requestId !== activeRequestRef.current) return;
      setActiveProjects(result.data);
    } catch {
      if (requestId === activeRequestRef.current) toast.error('Error al cargar proyectos');
    }
  }, [transparencyProjectRepo, toast, debouncedSearch, category]);

  const loadHistory = useCallback(async () => {
    const requestId = ++historyRequestRef.current;
    try {
      const result = await transparencyProjectRepo.getHistoryProjects({
        limit: 50,
        search: debouncedSearch || undefined,
        category: category || undefined,
      });
      if (requestId !== historyRequestRef.current) return;
      setHistoryProjects(result.data);
      setHistoryLoaded(true);
    } catch {
      if (requestId === historyRequestRef.current) toast.error('Error al cargar el historial');
    }
  }, [transparencyProjectRepo, toast, debouncedSearch, category]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadActive();
      setLoading(false);
    };
    init();
  }, [loadActive]);

  useEffect(() => {
    setHistoryLoaded(false);
  }, [debouncedSearch, category]);

  useEffect(() => {
    if (activeTab === 'history' && !historyLoaded) {
      setLoading(true);
      loadHistory().finally(() => setLoading(false));
    }
  }, [activeTab, historyLoaded, loadHistory]);

  const currentProjects = activeTab === 'active' ? activeProjects : historyProjects;
  const emptyMessage =
    activeTab === 'active' ? 'No hay proyectos activos por ahora.' : 'Aún no hay proyectos completados.';

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Transparencia</h2>
          <p className="text-slate-500 text-sm">Sigue el avance de las mejoras en tu comunidad.</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar proyecto..."
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as TransparencyProjectCategory | '')}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
        >
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'active' ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Activos
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'history' ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Historial
        </button>
      </div>

      {loading ? (
        <Loading />
      ) : currentProjects.length === 0 ? (
        <div className="text-center p-12 border-2 border-dashed border-slate-200 rounded-xl">
          <Folder size={48} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">{emptyMessage}</p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {currentProjects.map((project) => (
            <ProjectCard key={project.id} project={project} onClick={setSelectedProject} />
          ))}
        </div>
      )}

      {selectedProject && (
        <ProjectDetailModal project={selectedProject} onClose={() => setSelectedProject(null)} />
      )}
    </div>
  );
};
